import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { EnablementStatus } from '@squareup/shared-types-protos/squareup/channelcontrols/configuration/enums';
import type { FulfillmentConfiguration } from '@squareup/shared-types-protos/squareup/channelcontrols/configuration/models';
import { FulfillmentType } from '../../hooks/useFulfillmentConfiguration.js';
import { useActiveLocations } from '../../hooks/useLocations.js';
import { LocationSelectModal } from '../location-select-modal/location-select-modal.js';

export interface FulfillmentEnablementLocationModalProps {
  readonly fulfillmentType: FulfillmentType;
  readonly locationIdToFulfillmentConfigurationMap: Record<string, FulfillmentConfiguration>;
  readonly onSubmit: (selectedLocationIds: string[]) => Promise<void>;
  readonly onDismiss: () => void;
  readonly onToggleTracking?: (locationId: string, isEnabling: boolean) => void;
}

export function FulfillmentEnablementLocationModal({
  fulfillmentType,
  locationIdToFulfillmentConfigurationMap,
  onSubmit,
  onDismiss,
  onToggleTracking,
}: FulfillmentEnablementLocationModalProps) {
  const { t } = useTranslation();
  const { data: locationData, isPending: isLocationDataLoading } = useActiveLocations();

  const title = fulfillmentType === FulfillmentType.Pickup
    ? t('fulfillmentEnablementLocationModal.pickupTitle')
    : t('fulfillmentEnablementLocationModal.deliveryTitle');

  const locations = useMemo(
    () => locationData?.locations || [],
    [locationData?.locations],
  );

  const initialSelectedLocationIds = useMemo(
    () => locations
      .filter((loc) => {
        if (!loc.id) {
          return false;
        }
        const config = locationIdToFulfillmentConfigurationMap[loc.id];
        if (!config) {
          return false;
        }
        const settings = fulfillmentType === FulfillmentType.Pickup
          ? config.pickupSettings
          : config.deliverySettings;
        return settings?.enablementStatus === EnablementStatus.ENABLED;
      })
      .map((loc) => loc.id as string),
    [locations, locationIdToFulfillmentConfigurationMap, fulfillmentType],
  );

  const handleToggleTracking = (locationId: string, isSelected: boolean) => {
    if (onToggleTracking) {
      onToggleTracking(locationId, isSelected);
    }
  };

  return (
    <LocationSelectModal
      title={title}
      locations={locations}
      locationIdToFulfillmentConfigurationMap={locationIdToFulfillmentConfigurationMap}
      isLoading={isLocationDataLoading}
      initialSelectedLocationIds={initialSelectedLocationIds}
      onDismiss={onDismiss}
      onSubmit={onSubmit}
      onToggleTracking={handleToggleTracking}
    />
  );
}
